import { FractionDigits, splitNanoSecForDisplay } from "@/shared/time";

import { FractionalFormatOptions } from "./format";
import { getActiveDateTimeFormat } from "./registry";

/**
 * Renders an epoch-nanosecond instant in the user's chosen date/time preset,
 * keeping `fractionDigits` fractional-second digits.
 *
 * `Date`, and therefore every other formatter in this module, stops at the
 * millisecond. This entry point splits the nanoseconds into a `Date` for the
 * calendar fields and a digit string for everything below the second, then
 * asks the active format to substitute that string into its own
 * `fractionalSecond` field — so the preset's component order, its clock, and
 * the locale's decimal separator all survive a width `Intl` would refuse.
 *
 * Rounding runs before the split, so a fraction that carries advances the
 * calendar fields rather than overflowing the digits (see
 * {@link splitNanoSecForDisplay}).
 *
 * It lives in its own module rather than in `format.ts` because `shared/time`
 * imports this directory's `formatters`; importing `@/shared/time` back into
 * `format.ts` would close an import cycle.
 */
export function formatDateTimeNsWithOptions(
  ns: bigint,
  options: FractionalFormatOptions & { fractionDigits: FractionDigits },
): string {
  const { date, fraction } = splitNanoSecForDisplay(ns, options.fractionDigits);
  return getActiveDateTimeFormat().formatFractional(date, fraction, {
    timeZone: options.timeZone,
  });
}
